import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAdminStore } from '../../store/adminStore'
import Spinner from '../../components/common/Spinner'

const FILTERS = [
  { value: 'crowd_proposed', label: 'Needs Review', statsKey: 'crowd_proposed' },
  { value: 'published',      label: 'Approved',     statsKey: 'published' },
  { value: 'archived',       label: 'Rejected',     statsKey: 'archived' },
  { value: '',               label: 'All',          statsKey: null },
] as const

const STATUS_COLORS: Record<string, { bg: string; color: string }> = {
  crowd_proposed: { bg: '#f59e0b22', color: '#f59e0b' },
  published:      { bg: '#22c55e22', color: '#22c55e' },
  archived:       { bg: '#6b728022', color: '#9ca3af' },
  draft:          { bg: '#6b728022', color: '#9ca3af' },
}

const STATUS_LABELS: Record<string, string> = {
  crowd_proposed: 'Needs Review',
  published:      'Approved',
  archived:       'Rejected',
  draft:          'Draft',
}

export default function AdminProductsPage() {
  const navigate = useNavigate()
  const {
    crowdProducts, totalCrowdProducts, fetchCrowdProducts,
    fetchCategories, categories, isLoading, error,
    publishProduct, archiveProduct, bulkPublishProducts,
    stats, fetchStats,
  } = useAdminStore()

  const [search, setSearch] = useState('')
  const [searchDebounce, setSearchDebounce] = useState('')
  const [statusFilter, setStatusFilter] = useState('crowd_proposed')
  const [categoryFilter, setCategoryFilter] = useState('')
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [bulkBusy, setBulkBusy] = useState(false)
  const isFirstLoad = useRef(true)

  useEffect(() => {
    const t = setTimeout(() => setSearchDebounce(search), 300)
    return () => clearTimeout(t)
  }, [search])

  useEffect(() => {
    fetchCategories()
    fetchStats()
  }, [fetchCategories, fetchStats])

  useEffect(() => {
    setSelected(new Set())
    fetchCrowdProducts({
      skip: 0,
      search: searchDebounce || undefined,
      status: statusFilter || undefined,
      category_id: categoryFilter || undefined,
    }).finally(() => { isFirstLoad.current = false })
  }, [searchDebounce, statusFilter, categoryFilter]) // eslint-disable-line react-hooks/exhaustive-deps

  const hasMore = crowdProducts.length < totalCrowdProducts

  const handleLoadMore = () => {
    fetchCrowdProducts(
      {
        skip: crowdProducts.length,
        search: searchDebounce || undefined,
        status: statusFilter || undefined,
        category_id: categoryFilter || undefined,
      },
      true,
    )
  }

  const toggleSelect = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const toggleSelectAll = () => {
    if (selected.size === crowdProducts.length) {
      setSelected(new Set())
    } else {
      setSelected(new Set(crowdProducts.map((p) => p.id)))
    }
  }

  const handleBulkPublish = async () => {
    if (selected.size === 0) return
    setBulkBusy(true)
    try {
      await bulkPublishProducts(Array.from(selected))
      setSelected(new Set())
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : 'Failed')
    } finally {
      setBulkBusy(false)
    }
  }

  const handlePublish = async (productId: string, e: React.MouseEvent) => {
    e.stopPropagation()
    try {
      await publishProduct(productId)
      fetchStats()
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : 'Failed')
    }
  }

  const handleArchive = async (productId: string, e: React.MouseEvent) => {
    e.stopPropagation()
    try {
      await archiveProduct(productId)
      fetchStats()
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : 'Failed')
    }
  }

  const getCount = (statsKey: string | null): number | null => {
    if (!stats?.products_by_status) return null
    if (statsKey === null) return stats.total_products ?? null
    return stats.products_by_status[statsKey] ?? 0
  }

  if (isLoading && isFirstLoad.current) {
    return (
      <div className="flex items-center justify-center py-16">
        <Spinner />
      </div>
    )
  }

  return (
    <div className="pb-8">
      {/* Filter bar — always visible */}
      <div
        className="px-4 pt-3 pb-3 flex gap-2 overflow-x-auto"
        style={{ borderBottom: '1px solid var(--app-card-bg)' }}
      >
        {FILTERS.map(({ value, label, statsKey }) => {
          const isActive = statusFilter === value
          const count = getCount(statsKey)
          return (
            <button
              key={value}
              className="flex-shrink-0 flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-medium transition-all"
              style={{
                background: isActive ? 'var(--tg-theme-button-color)' : 'var(--app-card-bg)',
                color: isActive ? 'var(--tg-theme-button-text-color)' : 'var(--tg-theme-text-color)',
              }}
              onClick={() => setStatusFilter(value)}
            >
              {label}
              {count !== null && (
                <span
                  className="text-xs font-semibold px-1.5 py-0.5 rounded-full min-w-[20px] text-center"
                  style={{
                    background: isActive ? 'rgba(255,255,255,0.25)' : 'var(--app-page-bg)',
                    color: isActive ? 'var(--tg-theme-button-text-color)' : 'var(--tg-theme-text-color)',
                  }}
                >
                  {count}
                </span>
              )}
            </button>
          )
        })}
      </div>

      <div className="p-4 space-y-3">
        {/* Search */}
        <div
          className="flex items-center gap-2 rounded-xl px-3"
          style={{ background: 'var(--app-card-bg)' }}
        >
          <span className="opacity-40 text-sm">🔍</span>
          <input
            style={{
              background: 'transparent',
              color: 'var(--tg-theme-text-color)',
              border: 'none',
              padding: '10px 0',
              width: '100%',
              fontSize: '14px',
              outline: 'none',
            }}
            placeholder="Search products…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {search && (
            <button
              className="text-xl leading-none opacity-40"
              style={{ color: 'var(--tg-theme-text-color)' }}
              onClick={() => setSearch('')}
            >
              ×
            </button>
          )}
        </div>

        {/* Category chips */}
        {categories.length > 0 && (
          <div className="overflow-x-auto -mx-4 px-4">
            <div className="flex gap-2 pb-1">
              <button
                className="px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap"
                style={
                  categoryFilter === ''
                    ? { background: 'var(--tg-theme-link-color)', color: 'var(--tg-theme-button-text-color)' }
                    : { background: 'var(--app-card-bg)', color: 'var(--tg-theme-text-color)' }
                }
                onClick={() => setCategoryFilter('')}
              >
                All categories
              </button>
              {categories.map((c) => (
                <button
                  key={c.id}
                  className="px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap"
                  style={
                    categoryFilter === c.id
                      ? { background: 'var(--tg-theme-link-color)', color: 'var(--tg-theme-button-text-color)' }
                      : { background: 'var(--app-card-bg)', color: 'var(--tg-theme-text-color)' }
                  }
                  onClick={() => setCategoryFilter(categoryFilter === c.id ? '' : c.id)}
                >
                  {c.name}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Bulk actions bar */}
        {crowdProducts.length > 0 && (
          <div className="flex items-center gap-2">
            <button
              className="text-xs px-3 py-1.5 rounded-lg"
              style={{ background: 'var(--app-card-bg)', color: 'var(--tg-theme-text-color)' }}
              onClick={toggleSelectAll}
            >
              {selected.size === crowdProducts.length ? 'Deselect all' : 'Select all'}
            </button>
            {selected.size > 0 && (
              <button
                className="text-xs px-3 py-1.5 rounded-lg font-medium disabled:opacity-40"
                style={{ background: '#22c55e22', color: '#22c55e' }}
                disabled={bulkBusy}
                onClick={handleBulkPublish}
              >
                {bulkBusy ? 'Approving…' : `Approve ${selected.size} selected`}
              </button>
            )}
            <p className="text-xs opacity-40 ml-auto" style={{ color: 'var(--tg-theme-text-color)' }}>
              {totalCrowdProducts} shown
            </p>
          </div>
        )}

        {error && (
          <p className="text-sm text-center py-4" style={{ color: '#ef4444' }}>
            {error}
          </p>
        )}

        {/* Product list */}
        {!isLoading && crowdProducts.length === 0 && !error && (
          <div className="text-center py-16">
            <div className="text-5xl mb-3">
              {statusFilter === 'crowd_proposed' ? '✅' : '📦'}
            </div>
            <p className="text-sm opacity-40" style={{ color: 'var(--tg-theme-text-color)' }}>
              {statusFilter === 'crowd_proposed' && !search && !categoryFilter
                ? 'All caught up — nothing needs review'
                : 'No products match your filters'}
            </p>
          </div>
        )}

        <div className="space-y-2">
          {crowdProducts.map((product) => {
            const s = STATUS_COLORS[product.status ?? ''] ?? { bg: '#6b728022', color: '#9ca3af' }
            const isSelected = selected.has(product.id)
            return (
              <div
                key={product.id}
                className="rounded-xl p-3 cursor-pointer"
                style={{
                  background: 'var(--app-card-bg)',
                  outline: isSelected ? '2px solid var(--tg-theme-link-color)' : 'none',
                }}
                onClick={() => navigate(`/admin/products/${product.id}`)}
              >
                <div className="flex gap-3">
                  {/* Checkbox */}
                  <div
                    className="flex-shrink-0 self-start mt-0.5"
                    onClick={(e) => { e.stopPropagation(); toggleSelect(product.id) }}
                  >
                    <div
                      className="w-5 h-5 rounded flex items-center justify-center text-xs"
                      style={{
                        background: isSelected ? 'var(--tg-theme-link-color)' : 'var(--app-page-bg)',
                        color: 'white',
                      }}
                    >
                      {isSelected && '✓'}
                    </div>
                  </div>

                  {/* Thumbnail */}
                  <div
                    className="w-14 h-14 rounded-xl flex-shrink-0 overflow-hidden flex items-center justify-center text-2xl"
                    style={{ background: 'var(--app-page-bg)' }}
                  >
                    {product.thumbnail_url ? (
                      <img
                        src={product.thumbnail_url}
                        alt={product.name}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    ) : (
                      <span className="opacity-30">📷</span>
                    )}
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <p className="font-medium text-sm leading-tight" style={{ color: 'var(--tg-theme-text-color)' }}>
                        {product.name}
                      </p>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        {/* Language badge */}
                        {product.translations && Object.keys(product.translations).length > 0 && (
                          <span
                            className="text-xs px-1.5 py-0.5 rounded font-mono uppercase"
                            style={{ background: 'var(--app-page-bg)', color: 'var(--tg-theme-text-color)', opacity: 0.6 }}
                          >
                            {Object.keys(product.translations)[0]}
                          </span>
                        )}
                        {/* Only show status badge when showing "All" */}
                        {statusFilter === '' && (
                          <span
                            className="text-xs px-2 py-0.5 rounded-full font-medium"
                            style={{ background: s.bg, color: s.color }}
                          >
                            {STATUS_LABELS[product.status ?? ''] ?? product.status ?? 'Unknown'}
                          </span>
                        )}
                      </div>
                    </div>

                    {product.description && (
                      <p
                        className="text-xs mt-0.5 line-clamp-2 opacity-60"
                        style={{ color: 'var(--tg-theme-text-color)' }}
                      >
                        {product.description}
                      </p>
                    )}

                    {product.merchant_name && (
                      <p className="text-xs mt-0.5 opacity-50" style={{ color: 'var(--tg-theme-text-color)' }}>
                        🏪 {product.merchant_name}
                      </p>
                    )}
                  </div>
                </div>

                {/* Footer: price + action buttons */}
                <div
                  className="flex items-center justify-between mt-2 pt-2 gap-2"
                  style={{ borderTop: '1px solid var(--app-page-bg)' }}
                >
                  <span className="text-sm font-semibold" style={{ color: 'var(--tg-theme-text-color)' }}>
                    {product.price
                      ? `${product.price.toLocaleString()} ${product.price_currency ?? 'UZS'}`
                      : <span className="opacity-40 font-normal text-xs">No price</span>}
                  </span>
                  <div className="flex gap-1.5" onClick={(e) => e.stopPropagation()}>
                    {product.status !== 'published' && (
                      <button
                        className="text-xs px-2.5 py-1 rounded-lg font-medium"
                        style={{ background: '#22c55e22', color: '#22c55e' }}
                        onClick={(e) => handlePublish(product.id, e)}
                      >
                        Approve
                      </button>
                    )}
                    {product.status !== 'archived' && (
                      <button
                        className="text-xs px-2.5 py-1 rounded-lg font-medium"
                        style={{ background: '#ef444418', color: '#ef4444' }}
                        onClick={(e) => handleArchive(product.id, e)}
                      >
                        Reject
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )
          })}
        </div>

        {/* Load more */}
        {hasMore && (
          <button
            className="w-full py-2.5 rounded-xl text-sm font-medium disabled:opacity-40"
            style={{ background: 'var(--app-card-bg)', color: 'var(--tg-theme-link-color)' }}
            disabled={isLoading}
            onClick={handleLoadMore}
          >
            {isLoading ? 'Loading…' : `Load more (${totalCrowdProducts - crowdProducts.length} remaining)`}
          </button>
        )}
      </div>
    </div>
  )
}
